import { useId, useMemo, useState, type ReactNode } from 'react';
import { cx } from '../cx';
import { Icon } from './Icon';

export type SearchSuggestion = { value: string; label?: ReactNode; hint?: string };

const fold = (t: string) => t.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');

/**
 * Campo de busca com sugestões enquanto se digita (autocompletar) e um x para limpar. As sugestões são filtradas aqui, sem
 * acento e por pedaço da palavra; setas escolhem, Enter aplica, Esc fecha. `onPick` recebe o valor escolhido (sem ele, o
 * valor vai para `onChange`). Vazio mostra as primeiras sugestões quando `showOnEmpty`.
 */
export function SearchField({ value, onChange, onPick, suggestions = [], placeholder, max = 8, showOnEmpty, className, 'aria-label': ariaLabel, autoFocus }: {
  value: string; onChange: (v: string) => void; onPick?: (v: string) => void; suggestions?: SearchSuggestion[]; placeholder?: string; max?: number; showOnEmpty?: boolean; className?: string; 'aria-label'?: string; autoFocus?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [hot, setHot] = useState(0);
  const listId = useId();
  const shown = useMemo(() => {
    const q = fold(value.trim());
    if (!q && !showOnEmpty) return [];
    const seen = new Set<string>();
    return suggestions.filter((s) => {
      const k = fold(s.value);
      if (seen.has(k) || (q && (!k.includes(q) || k === q))) return false;
      seen.add(k);
      return true;
    }).sort((a, b) => Number(!fold(a.value).startsWith(q)) - Number(!fold(b.value).startsWith(q))).slice(0, max);
  }, [value, suggestions, max, showOnEmpty]);
  const pick = (v: string) => { setOpen(false); setHot(0); if (onPick) onPick(v); else onChange(v); };
  const visible = open && shown.length > 0;
  return (
    <div className={cx('rr-search', className)}>
      <Icon name="search" className="rr-search__icon" />
      <input
        className="rr-input rr-search__input" value={value} placeholder={placeholder} aria-label={ariaLabel} autoFocus={autoFocus}
        role="combobox" aria-expanded={visible} aria-controls={listId} aria-autocomplete="list"
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHot(0); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { if (visible) setOpen(false); else if (value) onChange(''); return; }
          if (!visible) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setHot((h) => (h + 1) % shown.length); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHot((h) => (h - 1 + shown.length) % shown.length); }
          else if (e.key === 'Enter' && shown[hot]) { e.preventDefault(); pick(shown[hot].value); }
        }}
      />
      {value && <button type="button" className="rr-search__clear" aria-label="limpar a busca" onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange(''); setOpen(false); }}><Icon name="x" /></button>}
      {visible && (
        <ul className="rr-search__list" id={listId} role="listbox">
          {shown.map((s, i) => (
            <li key={s.value} role="option" aria-selected={i === hot} className={cx('rr-search__opt', i === hot && 'rr-search__opt--hot')} onMouseEnter={() => setHot(i)} onMouseDown={(e) => { e.preventDefault(); pick(s.value); }}>
              <span>{s.label ?? s.value}</span>{s.hint && <small>{s.hint}</small>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
